import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Table, Alert, Spinner, Form } from 'react-bootstrap';
import { getMyReferrals } from '../services/api';

interface ReferralEntry {
  referred_user_id: string;
  referred_username: string;
  applied_at: string;
  basic_reward: number;
  premium_bonus_awarded: boolean;
  bonus_reward: number;
  total_reward: number;
}

interface ReferralResponse {
  referral_code: string;
  total_earned: number;
  referrals: ReferralEntry[];
}

const ReferralPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [referralData, setReferralData] = useState<ReferralResponse | null>(null);
  const [error, setError] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<string>('');

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getMyReferrals();
      setReferralData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const referralLink = referralData ? `${window.location.origin}/signup?ref=${referralData.referral_code}` : '';

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink)
        .then(() => {
          setCopySuccess('Referral link copied!');
          setTimeout(() => setCopySuccess(''), 3000);
        })
        .catch(() => {
          setError('Failed to copy referral link');
        });
  };

  return (
      <Container className="py-4">
        <Row className="mb-4">
          <Col>
            <h1>Referral Program</h1>
            <p>
              Share your referral code with friends and earn rewards.
              You get $2 when someone signs up using your code,
              plus an extra $5 bonus when they subscribe to premium features.
            </p>
          </Col>
        </Row>

        {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              {error}
            </Alert>
        )}

        {loading ? (
            <Row className="justify-content-center">
              <Spinner animation="border" role="status" className="my-4" />
            </Row>
        ) : (
            referralData && (
                <>
                  <Row className="mb-4">
                    <Col md={6}>
                      <Card className="shadow-sm">
                        <Card.Body>
                          <h5>Your Referral Code</h5>
                          <Form.Control type="text" value={referralData.referral_code} readOnly />
                          <small className="text-muted">
                            Share this code with friends to earn rewards.
                          </small>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="shadow-sm">
                        <Card.Body>
                          <h5>Your Referral Link</h5>
                          <Form.Control type="text" value={referralLink} readOnly />
                          <Button variant="outline-primary" className="mt-2" onClick={copyToClipboard}>
                            Copy Link
                          </Button>
                          {copySuccess && (
                              <Alert variant="success" className="mt-2" onClose={() => setCopySuccess('')} dismissible>
                                {copySuccess}
                              </Alert>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  <Row className="mb-4">
                    <Col>
                      <Card className="shadow-sm">
                        <Card.Header>
                          <h5>Referral History</h5>
                        </Card.Header>
                        <Card.Body>
                          <p>
                            <strong>Total Earned:</strong> ${referralData.total_earned.toFixed(2)}
                          </p>
                          {referralData.referrals.length > 0 ? (
                              <Table responsive bordered hover>
                                <thead>
                                <tr>
                                  <th>Referred Username</th>
                                  <th>Applied At</th>
                                  <th>Basic Reward ($2)</th>
                                  <th>Bonus Reward ($5)</th>
                                  <th>Total Reward</th>
                                </tr>
                                </thead>
                                <tbody>
                                {referralData.referrals.map((ref) => (
                                    <tr key={ref.referred_user_id}>
                                      <td>{ref.referred_username}</td>
                                      <td>{new Date(ref.applied_at).toLocaleString()}</td>
                                      <td>${ref.basic_reward.toFixed(2)}</td>
                                      <td>${ref.bonus_reward.toFixed(2)}</td>
                                      <td>${ref.total_reward.toFixed(2)}</td>
                                    </tr>
                                ))}
                                </tbody>
                              </Table>
                          ) : (
                              <p>No referrals yet. Start sharing your code!</p>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </>
            )
        )}
      </Container>
  );
};

export default ReferralPage;
